import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { slugifyCategory, formatCatalogDisplayName } from '../utils/categoryMenuTree';
import { useTouchFriendlyUI } from '../utils/useTouchFriendlyUI';
import './NestedCategoryFlyout.css';

function scrollPanelIntoView(panelEl) {
  if (!panelEl) return;
  window.requestAnimationFrame(() => {
    panelEl.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
  });
}

function NestedCategoryRow({
  label,
  hasChildren,
  isActive,
  isDisabled,
  useTapMenus,
  expandAriaLabel,
  onHoverActivate,
  onTapActivate,
  onNavigate,
}) {
  const handleLabelClick = () => {
    if (isDisabled) return;
    if (useTapMenus && hasChildren) {
      onTapActivate();
      return;
    }
    if (!hasChildren) onNavigate();
  };

  const handleExpandClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isDisabled) return;
    onTapActivate();
  };

  return (
    <div
      className={`nested-category-row${isActive ? ' nested-category-row--active' : ''}${useTapMenus && hasChildren ? ' nested-category-row--tappable' : ''}`}
      onMouseEnter={!useTapMenus && hasChildren ? onHoverActivate : undefined}
    >
      <button
        type="button"
        className="nested-category-item"
        role="menuitem"
        disabled={isDisabled}
        aria-expanded={hasChildren ? isActive : undefined}
        onClick={handleLabelClick}
      >
        <span>{label}</span>
        {hasChildren && !useTapMenus ? (
          <span className="nested-category-chevron" aria-hidden>
            ›
          </span>
        ) : null}
      </button>
      {hasChildren && useTapMenus ? (
        <button
          type="button"
          className="nested-category-expand-btn"
          aria-expanded={isActive}
          aria-label={expandAriaLabel}
          onClick={handleExpandClick}
        >
          <span className="nested-category-chevron" aria-hidden>
            ›
          </span>
        </button>
      ) : null}
    </div>
  );
}

/** Single-category nested panels (panel 2 groups/leaves → panel 3 leaves). */
export function NestedCategorySubmenu({ category, treeItems = [], onClose, className = '' }) {
  const navigate = useNavigate();
  const useTapMenus = useTouchFriendlyUI(768);
  const closeTimerRef = useRef(null);
  const panel3Ref = useRef(null);
  const [activeGroupId, setActiveGroupId] = useState(null);

  const cancelClose = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const scheduleClose = useCallback(() => {
    if (useTapMenus) return;
    cancelClose();
    closeTimerRef.current = setTimeout(() => setActiveGroupId(null), 120);
  }, [useTapMenus, cancelClose]);

  useEffect(() => () => cancelClose(), [cancelClose]);

  useEffect(() => {
    if (!useTapMenus || !activeGroupId) return;
    scrollPanelIntoView(panel3Ref.current);
  }, [activeGroupId, useTapMenus]);

  if (!treeItems.length || !category) return null;

  const catSlug = category.slug || slugifyCategory(category.name);
  const activeGroup = treeItems.find((g) => String(g.id) === String(activeGroupId));
  const panel3Items = activeGroup?.isGroup ? activeGroup.children || [] : [];

  const goToSub = (sub) => {
    const subSlug = sub.slug || slugifyCategory(sub.name);
    navigate(`/category/${catSlug}/${subSlug}`);
    onClose?.();
  };

  const toggleGroup = (groupId, hasFlyout) => {
    if (!hasFlyout) return;
    setActiveGroupId((current) => (current === groupId ? null : groupId));
  };

  return (
    <div
      className={`nested-category-flyout nested-category-flyout--inline${useTapMenus ? ' nested-category-flyout--tap' : ''}${className ? ` ${className}` : ''}`}
      onMouseLeave={scheduleClose}
    >
      <div className="nested-category-panel nested-category-panel--sub" role="menu" aria-label={`${category.name} subcategories`}>
        {treeItems.map((item) => {
          const hasFlyout = item.isGroup && (item.children?.length > 0);
          const itemId = String(item.id);
          const isActive = activeGroupId === itemId;
          return (
            <NestedCategoryRow
              key={item.id}
              label={formatCatalogDisplayName(item.name)}
              hasChildren={hasFlyout}
              isActive={isActive}
              isDisabled={false}
              useTapMenus={useTapMenus}
              expandAriaLabel={`Show ${formatCatalogDisplayName(item.name)} subcategories`}
              onHoverActivate={() => {
                cancelClose();
                setActiveGroupId(hasFlyout ? itemId : null);
              }}
              onTapActivate={() => toggleGroup(itemId, hasFlyout)}
              onNavigate={() => goToSub(item)}
            />
          );
        })}
      </div>

      {activeGroup && panel3Items.length > 0 ? (
        <div
          ref={panel3Ref}
          className="nested-category-panel nested-category-panel--nested"
          role="menu"
          aria-label={`${activeGroup.name} items`}
        >
          {panel3Items.map((leaf) => (
            <div key={leaf.id} className="nested-category-row">
              <button type="button" className="nested-category-item" role="menuitem" onClick={() => goToSub(leaf)}>
                <span>{formatCatalogDisplayName(leaf.name)}</span>
              </button>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function NestedCategoryFlyout({
  categories = [],
  menuTreeByCategory = {},
  onClose,
  className = '',
  anchor = 'left',
}) {
  const navigate = useNavigate();
  const useTapMenus = useTouchFriendlyUI(768);
  const closeTimerRef = useRef(null);
  const panel2Ref = useRef(null);
  const panel3Ref = useRef(null);
  const [activeCategoryId, setActiveCategoryId] = useState(null);
  const [activeGroupId, setActiveGroupId] = useState(null);

  const cancelClose = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const scheduleClose = useCallback(() => {
    if (useTapMenus) return;
    cancelClose();
    closeTimerRef.current = setTimeout(() => {
      setActiveCategoryId(null);
      setActiveGroupId(null);
    }, 120);
  }, [useTapMenus, cancelClose]);

  useEffect(() => () => cancelClose(), [cancelClose]);

  useEffect(() => {
    if (!useTapMenus) return;
    if (activeGroupId) {
      scrollPanelIntoView(panel3Ref.current);
      return;
    }
    if (activeCategoryId) {
      scrollPanelIntoView(panel2Ref.current);
    }
  }, [activeCategoryId, activeGroupId, useTapMenus]);

  const goToSubcategory = (category, sub) => {
    const catSlug = category.slug || slugifyCategory(category.name);
    const subSlug = sub.slug || slugifyCategory(sub.name);
    navigate(`/category/${catSlug}/${subSlug}`);
    onClose?.();
  };

  const activeCategory = categories.find((c) => String(c.id || c._id) === String(activeCategoryId));
  const panel2Items = activeCategoryId ? menuTreeByCategory[String(activeCategoryId)] || [] : [];
  const activeGroup = panel2Items.find((g) => String(g.id) === String(activeGroupId));
  const panel3Items = activeGroup?.isGroup ? activeGroup.children || [] : [];

  const toggleCategory = (catId, hasChildren) => {
    if (!hasChildren) return;
    setActiveGroupId(null);
    setActiveCategoryId((current) => (current === catId ? null : catId));
  };

  const toggleGroup = (groupId, hasFlyout) => {
    if (!hasFlyout) return;
    setActiveGroupId((current) => (current === groupId ? null : groupId));
  };

  return (
    <div
      className={`nested-category-flyout nested-category-flyout--${anchor}${useTapMenus ? ' nested-category-flyout--tap' : ''}${className ? ` ${className}` : ''}`}
      onMouseLeave={scheduleClose}
    >
      <div className="nested-category-panel nested-category-panel--main" role="menu" aria-label="Main categories">
        {categories.map((cat) => {
          const catId = String(cat.id || cat._id);
          const isActive = activeCategoryId === catId;
          const tree = menuTreeByCategory[catId] || [];
          const hasChildren = tree.length > 0;
          return (
            <NestedCategoryRow
              key={catId}
              label={formatCatalogDisplayName(cat.name)}
              hasChildren={hasChildren}
              isActive={isActive}
              useTapMenus={useTapMenus}
              expandAriaLabel={`Show ${formatCatalogDisplayName(cat.name)} subcategories`}
              onHoverActivate={() => {
                cancelClose();
                setActiveCategoryId(catId);
                setActiveGroupId(null);
              }}
              onTapActivate={() => toggleCategory(catId, hasChildren)}
              onNavigate={() => {
                navigate(`/category/${cat.slug || slugifyCategory(cat.name)}`);
                onClose?.();
              }}
            />
          );
        })}
      </div>

      {activeCategory && panel2Items.length > 0 ? (
        <div
          ref={panel2Ref}
          className="nested-category-panel nested-category-panel--sub"
          role="menu"
          aria-label={`${activeCategory.name} subcategories`}
        >
          {panel2Items.map((item) => {
            const hasFlyout = item.isGroup && (item.children?.length > 0);
            const itemId = String(item.id);
            const isActive = activeGroupId === itemId;
            return (
              <NestedCategoryRow
                key={item.id}
                label={formatCatalogDisplayName(item.name)}
                hasChildren={hasFlyout}
                isActive={isActive}
                isDisabled={false}
                useTapMenus={useTapMenus}
                expandAriaLabel={`Show ${formatCatalogDisplayName(item.name)} items`}
                onHoverActivate={() => {
                  cancelClose();
                  setActiveGroupId(hasFlyout ? itemId : null);
                }}
                onTapActivate={() => toggleGroup(itemId, hasFlyout)}
                onNavigate={() => goToSubcategory(activeCategory, item)}
              />
            );
          })}
        </div>
      ) : null}

      {activeGroup && panel3Items.length > 0 ? (
        <div
          ref={panel3Ref}
          className="nested-category-panel nested-category-panel--nested"
          role="menu"
          aria-label={`${activeGroup.name} items`}
        >
          {panel3Items.map((leaf) => (
            <div key={leaf.id} className="nested-category-row">
              <button
                type="button"
                className="nested-category-item"
                role="menuitem"
                onClick={() => goToSubcategory(activeCategory, leaf)}
              >
                <span>{formatCatalogDisplayName(leaf.name)}</span>
              </button>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default NestedCategoryFlyout;
